// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the meeting dialog (`docs/SHARED_AR_PLAN.md`), in two uses:
 *
 *   - Tools → Start a meeting (`openMeetingPanel`): creates a meeting,
 *     then offers its invitations and a "Start presenting" button;
 *   - the meeting line (`openMeetingSharePanel`, from roomChip): the
 *     invitations again, at any point during the meeting.
 *
 * Invitations go out by mail app, Gmail, Outlook, the share sheet or the
 * clipboard (`meetingShare.ts`). There are two: the participants' link,
 * which anyone may forward, and the moderator's, which carries a seat.
 * The presenter's link is never shown as text — it is the "Start
 * presenting" button — because a link that is shown is a link that gets
 * pasted somewhere.
 *
 * A created meeting's links are kept in this browser (`rememberMeeting`),
 * which is how the presenter's meeting line can offer the moderator's
 * link later. Anyone else's meeting line offers the participants' link
 * only: it is derived from the room code, and is no secret.
 */

import { t } from '../i18n'
import { createMeeting, rememberMeeting, rememberedMeeting, type MeetingLinks } from '../services/meetingHost'
import type { RoomSeat } from '../services/roomProtocol'
import { invitation, shareButtons, type InviteKind, formatRoomCode } from './meetingShare'
import { drawQr } from './qrCode'

let closeOpenPanel: (() => void) | null = null

/** Close the dialog if it is open. */
export function closeMeetingPanel(): void {
  closeOpenPanel?.()
}

interface MeetingPanelOptions {
  /** Create the meeting; injectable for tests. */
  create?: typeof createMeeting
  /** Go to an address; injectable for tests. */
  navigate?: (url: string) => void
}

interface Dialog {
  body: HTMLElement
  status: HTMLElement
  close: () => void
}

/** The dialog's frame: title, close button, body, status line. */
function openDialog(titleText: string, trigger: HTMLElement | null | undefined): Dialog | null {
  if (closeOpenPanel) return null
  const backdrop = document.createElement('div')
  backdrop.id = 'meeting-backdrop'
  const panel = document.createElement('div')
  panel.id = 'meeting-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'meeting-panel-title')

  const header = document.createElement('div')
  header.className = 'meeting-panel-header'
  const title = document.createElement('h2')
  title.id = 'meeting-panel-title'
  title.textContent = titleText
  const closeButton = document.createElement('button')
  closeButton.type = 'button'
  closeButton.className = 'meeting-panel-close'
  closeButton.setAttribute('aria-label', t('meeting.panel.close.aria'))
  closeButton.textContent = '✕'
  header.append(title, closeButton)

  const body = document.createElement('div')
  body.className = 'meeting-panel-body'
  const status = document.createElement('p')
  status.className = 'meeting-panel-status'
  status.setAttribute('role', 'status')

  panel.append(header, body, status)
  document.body.append(backdrop, panel)

  let closed = false
  const close = (): void => {
    if (closed) return
    closed = true
    closeOpenPanel = null
    panel.remove()
    backdrop.remove()
    trigger?.focus()
  }
  closeButton.addEventListener('click', close)
  backdrop.addEventListener('click', close)
  panel.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      ev.preventDefault()
      close()
    }
  })
  closeOpenPanel = close
  return { body, status, close }
}

/** One invitation: what it is, its link, and the ways to send it. */
function inviteSection(
  kind: InviteKind,
  link: string,
  code: string,
  expiresAt: number | null,
  status: HTMLElement,
): HTMLElement {
  const section = document.createElement('section')
  section.className = 'meeting-invite'
  const heading = document.createElement('h3')
  heading.textContent = t(kind === 'moderator' ? 'meeting.invite.moderator.heading' : 'meeting.invite.audience.heading')
  const note = document.createElement('p')
  note.className = 'meeting-panel-note'
  note.textContent = t(kind === 'moderator' ? 'meeting.invite.moderator.note' : 'meeting.invite.audience.note')
  const field = document.createElement('input')
  field.type = 'text'
  field.readOnly = true
  field.value = link
  field.setAttribute('aria-label', heading.textContent)
  field.addEventListener('focus', () => field.select())
  const buttons = shareButtons(invitation(kind, link, code, expiresAt), link, (message) => {
    status.textContent = message
    if (message === link) field.select()
  })
  section.append(heading, note, field)
  // AYNI: participants can also type the code at the site (the welcome page's join box).
  if (kind === 'audience') section.append(codeBlock(link, code))
  section.append(buttons, qrBlock(kind, link))
  return section
}

/** The participants' code, large, with where to type it. */
function codeBlock(link: string, code: string): HTMLElement {
  const block = document.createElement('div')
  block.className = 'meeting-code-block'
  let site = ''
  try {
    site = new URL(link).host
  } catch {
    /* no site to name */
  }
  const line = document.createElement('p')
  line.className = 'meeting-panel-note'
  line.textContent = t('meeting.invite.codeLine', { site })
  const value = document.createElement('p')
  value.className = 'meeting-code'
  value.textContent = formatRoomCode(code)
  value.setAttribute('aria-label', t('meeting.invite.codeAria', { code: code.split('').join(' ') }))
  block.append(line, value)
  return block
}

/**
 * A QR code of the link, to scan with a phone. The participants' code is
 * shown at once — putting it on the projector is the point. The
 * moderator's link is a seat, so its code waits behind a button and is not
 * shown to a room by accident.
 */
function qrBlock(kind: InviteKind, link: string): HTMLElement {
  const block = document.createElement('div')
  block.className = 'meeting-qr-block'
  const qr = document.createElement('div')
  qr.className = 'meeting-qr'
  qr.setAttribute('role', 'img')
  qr.setAttribute('aria-label', t(kind === 'moderator' ? 'meeting.qr.moderatorAria' : 'meeting.qr.audienceAria'))
  const draw = (): void => {
    void drawQr(qr, link).catch(() => {
      qr.remove() // no encoder (offline): the link and the buttons still work
    })
  }
  if (kind === 'audience') {
    block.append(qr, Object.assign(document.createElement('p'), { className: 'meeting-panel-note', textContent: t('meeting.qr.audienceCaption') }))
    draw()
    return block
  }
  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'meeting-qr-toggle'
  toggle.textContent = t('meeting.qr.show')
  toggle.setAttribute('aria-expanded', 'false')
  qr.hidden = true
  toggle.addEventListener('click', () => {
    const showing = qr.hidden
    if (showing && !qr.firstChild) draw()
    qr.hidden = !showing
    toggle.textContent = t(showing ? 'meeting.qr.hide' : 'meeting.qr.show')
    toggle.setAttribute('aria-expanded', String(showing))
  })
  block.append(toggle, qr)
  return block
}

/** The invitations of a meeting this browser created, and optionally the way in for its presenter. */
function showMeeting(dialog: Dialog, meeting: MeetingLinks, present: (() => void) | null): void {
  const sections: HTMLElement[] = [
    inviteSection('audience', meeting.links.audience, meeting.code, meeting.expiresAt, dialog.status),
    inviteSection('moderator', meeting.links.moderator, meeting.code, meeting.expiresAt, dialog.status),
  ]
  const note = document.createElement('p')
  note.className = 'meeting-panel-note'
  note.textContent = t('meeting.panel.lasts', {
    time: new Date(meeting.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  })
  sections.push(note)
  // AYNI: an Education host's class reads values, drops pins and sees the graph on its own devices.
  if (meeting.lendsData) {
    const lend = document.createElement('p')
    lend.className = 'meeting-panel-note'
    lend.textContent = t('meeting.panel.lendsData')
    sections.push(lend)
  }
  if (present) {
    const start = document.createElement('button')
    start.type = 'button'
    start.className = 'meeting-panel-primary'
    start.textContent = t('meeting.panel.present')
    start.addEventListener('click', present)
    sections.push(start)
  }
  dialog.body.replaceChildren(...sections)
  dialog.status.textContent = ''
  ;(dialog.body.querySelector('button') as HTMLButtonElement | null)?.focus()
}

/** Tools → Start a meeting. `trigger` gets the focus back when it closes. */
export function openMeetingPanel(trigger?: HTMLElement | null, opts: MeetingPanelOptions = {}): void {
  const create = opts.create ?? createMeeting
  const navigate = opts.navigate ?? ((url: string) => { window.location.assign(url) })
  const dialog = openDialog(t('meeting.panel.title'), trigger)
  if (!dialog) return

  const intro = document.createElement('p')
  intro.textContent = t('meeting.panel.intro')
  const start = document.createElement('button')
  start.type = 'button'
  start.className = 'meeting-panel-primary'
  start.textContent = t('meeting.panel.create')
  dialog.body.append(intro, start)
  start.focus()

  start.addEventListener('click', () => {
    start.disabled = true
    dialog.status.textContent = t('meeting.panel.creating')
    void create().then((result) => {
      if (!document.body.contains(dialog.body)) return
      if (!result.ok) {
        start.disabled = false
        dialog.status.textContent = t(
          result.reason === 'unauthorized'
            ? 'meeting.panel.error.unauthorized'
            : result.reason === 'plan_cannot_host'
              ? 'meeting.panel.error.planCannotHost'
              : 'meeting.panel.error.unavailable',
        )
        return
      }
      rememberMeeting(result.meeting)
      showMeeting(dialog, result.meeting, () => navigate(result.meeting.links.presenter))
    })
  })
}

/**
 * The meeting line's dialog: the invitations to room `code`. The
 * presenter of a meeting this browser created gets both; anyone else
 * the participants' link, which is the room's own address.
 */
export function openMeetingSharePanel(
  code: string,
  seat: RoomSeat | null,
  trigger?: HTMLElement | null,
  origin: string = window.location.origin,
): void {
  const remembered = rememberedMeeting(code)
  if (remembered && seat === 'presenter') {
    const dialog = openDialog(t('meeting.share.title'), trigger)
    if (dialog) showMeeting(dialog, remembered, null)
    return
  }
  const dialog = openDialog(t(seat === null ? 'meeting.share.roomTitle' : 'meeting.share.title'), trigger)
  if (!dialog) return
  const link = `${origin}/?room=${code}`
  dialog.body.replaceChildren(inviteSection('audience', link, code, remembered?.expiresAt ?? null, dialog.status))
  ;(dialog.body.querySelector('button') as HTMLButtonElement | null)?.focus()
}
